import React from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/button.tsx";
import { ErrorBanner } from "../components/error-banner.tsx";
import { FieldGroup } from "../components/field-group.tsx";
import { Input } from "../components/input.tsx";

const ERROR_MESSAGES: Record<string, string> = {
  email_required: "Enter an email to receive a sign-in link.",
  send_failed: "We couldn't send the sign-in link. Try again in a moment.",
};

export function SignIn() {
  const [searchParams] = useSearchParams();
  const errorKey = searchParams.get("login_error");
  const errorMessage = errorKey ? (ERROR_MESSAGES[errorKey] ?? errorKey) : null;

  return (
    <div className="mx-auto max-w-2xl px-6 py-20">
      <header>
        <h1 className="font-mono text-5xl font-bold uppercase tracking-[0.05em] leading-none">
          AUTHED.CO
        </h1>
        <p className="mt-6 max-w-md text-base leading-relaxed text-ink">
          Simple OpenID sign-in for development, personal sites, and client
          work.
        </p>
      </header>

      <ErrorBanner message={errorMessage} className="mt-10 text-left" />

      <form method="post" action="/auth/login" className="mt-10">
        <FieldGroup>
          <Input
            type="email"
            name="email"
            autoComplete="email"
            required
            placeholder="you@example.com"
          />
          <Button type="submit">Get started</Button>
        </FieldGroup>
      </form>

      <section className="mt-20">
        <h2 className="border-b-2 border-ink pb-2 font-mono text-xs font-bold uppercase tracking-[0.2em]">
          What is this?
        </h2>
        <p className="mt-4 text-sm leading-relaxed">
          authed.co puts a sign-in screen in front of any OIDC-aware app. You
          issue access codes that grant access to your application. The user
          enters their code; your app receives a signed JWT identifying them.
        </p>
        <p className="mt-3 text-sm leading-relaxed">
          authed.co isn't designed to sit in front of high-value data, but it's
          better than "hard-to-guess" URLs for low-risk use cases, and works
          well as a mock signin option for development and testing.
        </p>
      </section>

      <a
        href="https://demo.authed.co"
        className="group mt-12 flex flex-wrap items-center justify-between gap-x-6 gap-y-1 border-2 border-ink p-5 no-underline hover:bg-ink hover:text-canvas"
      >
        <span className="font-mono text-sm font-bold uppercase tracking-wider">
          Try the demo →
        </span>
        <span className="font-mono text-xs text-muted group-hover:text-canvas">
          access code:{" "}
          <code className="font-mono text-ink group-hover:text-canvas">
            letmeinletmein
          </code>
        </span>
      </a>

      <section className="mt-16">
        <h2 className="border-b-2 border-ink pb-2 font-mono text-xs font-bold uppercase tracking-[0.2em]">
          FAQ
        </h2>
        <div className="mt-4 divide-y-2 divide-faint">
          <FaqItem question="What's an access code?">
            A 12+ character secret you create in the dashboard and map to a user's
            email - anyone who enters the code signs in as that user.
          </FaqItem>
          <FaqItem question="How do I integrate authed.co?">
            Use any OIDC-compatible library (e.g.
            <a href="https://github.com/authts/oidc-client-ts">oidc-client-ts</a>,&nbsp;
            <a href="https://github.com/authlib/authlib">authlib</a>); on your authed.co dashboard you'll
            get all the config values (client ID + secret, issuer URL, JWKS
            endpoint, etc.) to plug in.<br /><br />
            authed.co can be used as <a href="https://developers.cloudflare.com/learning-paths/clientless-access/initial-setup/configure-idp/">an external IdP with tools like Cloudflare Zero Trust</a>.
          </FaqItem>
          <FaqItem question="What about the email_verified claim?">
            Always{" "} <code className="font-mono text-xs">false</code> - since
            we don't send email confirmations, we can't verify that a code hasn't
            been shared.
          </FaqItem>
          <FaqItem question="Is authed.co secure?">
            authed.co is roughly as secure as the access codes you create, and
            how you choose to share them with your users. Access codes
            are hashed & peppered (HMAC-SHA256), and never stored or transmitted
            in plain text. If someone guesses or leaks an access code you
            create, they will be able to sign in - so we encourage using long,
            random codes.<br /><br />
            We don't verify users' email addresses or identities,
            so this is fundamentally less secure than an email link or proving
            ownership via social login.
          </FaqItem>
          <FaqItem question="How much does authed.co cost?">
            authed.co is currently free.
          </FaqItem>
        </div>
      </section>
    </div>
  );
}

function FaqItem({
  question,
  children,
}: {
  question: string;
  children: React.ReactNode;
}) {
  return (
    <details className="group py-4 [&::-webkit-details-marker]:hidden">
      <summary className="flex cursor-pointer list-none items-center justify-between font-mono text-xs font-bold uppercase tracking-wider hover:text-accent">
        {question}
        <span className="ml-2 text-muted transition-transform group-open:rotate-45">
          +
        </span>
      </summary>
      <div className="mt-3 text-sm leading-relaxed text-ink normal-case">
        {children}
      </div>
    </details>
  );
}
